import type { IPackageManager } from '@wix/package-manager';
import { gitCommit, gitInit } from '@wix/package-manager';
import { mkdir } from 'node:fs/promises';
import { CliError, CliErrorCode } from '@wix/cli-error';
import React, { useCallback, useMemo } from 'react';
import type { FC } from 'react';
import { Alert, Badge, Box, Text, useAsync } from '@wix/cli-ui-kit';
import { match } from 'variant';
import { generator } from '@wix/gena';
import { join } from 'node:path';
import { updateEnvFile, writeJson } from '@wix/cli-fs';
import { generateAgentConfigs, agentConfigsTaskText } from '@wix/agent-configs';
import { getWixConfigFilePath } from '@wix/cli-core-definitions';
import { getEnvFilePath } from '@wix/cli-astro-definitions';
import { extend } from '@wix/cli-project-extender';
import type { Task, TaskStatus } from '../task-runner.js';
import { useTaskRunner } from '../task-runner.js';
import { TaskList } from './TaskList.js';
import { fetchTemplate } from '../services/generator/template-fetcher.js';
import type { ProjectData } from '../hooks/create-project.js';
import type {
  CommandOptions,
  TemplateSource,
} from '../parse-command-options.js';

const headlessTemplatesGitUrl = 'https://github.com/wix/headless-templates.git';

async function resolveTemplateFolder(
  templatePathOverride: string | undefined,
  selectedTemplate: TemplateSource
): Promise<string> {
  if (templatePathOverride) {
    return templatePathOverride;
  }

  return match(selectedTemplate, {
    HeadlessApp: ({ gitPath }) =>
      fetchTemplate(headlessTemplatesGitUrl, gitPath),
    GitRepo: ({ url, path }) => fetchTemplate(url, path),
    Local: ({ path }) => path,
  });
}

const GenerationProgress: FC<{
  tasks: TaskStatus[];
  totalTaskCount: number;
}> = ({ tasks, totalTaskCount }) => {
  return (
    <>
      <Box marginBottom={1}>
        <Text>Generating your headless site...</Text>
      </Box>

      <TaskList tasks={tasks} totalTaskCount={totalTaskCount} />
    </>
  );
};

export const GenerateProject: FC<{
  packageManager: IPackageManager;
  targetParentFolder: string;
  folderName: string;
  businessId: string;
  projectData: ProjectData;
  commandOptions: CommandOptions;
  selectedTemplate: TemplateSource;
  onDone: (isDone: boolean) => void;
}> = ({
  packageManager,
  targetParentFolder,
  folderName,
  businessId,
  projectData,
  commandOptions,
  selectedTemplate,
  onDone,
}) => {
  const { execute: executeTaskRunner, status: taskRunnerStatus } =
    useTaskRunner();

  const packageFolder = useMemo(
    () => join(targetParentFolder, folderName),
    [folderName, targetParentFolder]
  );

  const generateProject = useCallback(async () => {
    try {
      await mkdir(packageFolder, { recursive: true });
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToCreateProjectFolder({ packageFolder }),
        cause: e,
      });
    }

    const templateFolder = await resolveTemplateFolder(
      commandOptions.templatePath,
      selectedTemplate
    );

    await generator({
      template: templateFolder,
      cwd: packageFolder,
      data: {
        templateParams: commandOptions.templateParams,
      },
    });

    const wixConfigFilePath = getWixConfigFilePath(packageFolder);
    const envFilePath = getEnvFilePath(packageFolder);

    await writeJson(
      wixConfigFilePath,
      {
        appId: projectData.projectId,
        siteId: businessId,
      },
      { spaces: 2 }
    );

    await updateEnvFile(envFilePath, projectData.environmentVariables);

    await packageManager.setup(packageFolder);
  }, [
    businessId,
    packageFolder,
    packageManager,
    projectData,
    selectedTemplate,
    commandOptions.templateParams,
    commandOptions.templatePath,
  ]);

  const extendProject = useCallback(async () => {
    await extend(packageFolder);
  }, [packageFolder]);

  const initializeGit = useCallback(async () => {
    try {
      await gitInit(packageFolder);
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToInitializeGitRepo(),
        cause: e,
      });
    }
  }, [packageFolder]);

  const installDependencies = useCallback(async () => {
    try {
      await packageManager.runInstall(packageFolder);
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToInstallDependencies(),
        cause: e,
      });
    }
  }, [packageFolder, packageManager]);

  const commitToGit = useCallback(async () => {
    try {
      await gitCommit(packageFolder);
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToCommitChanges(),
        cause: e,
      });
    }
  }, [packageFolder]);

  const tasks = useMemo(() => {
    const result = [
      {
        action: generateProject,
        loadingText: 'Generating project...',
        successText: 'Project generated.',
      },
      {
        action: extendProject,
        loadingText: 'Extending project template...',
        successText: 'The project template has been extended.',
      },
    ] satisfies Task[];

    if (!commandOptions.skipGit) {
      result.push({
        action: initializeGit,
        loadingText: 'Initializing git repository...',
        successText: 'Git repository initialized.',
      });
    }

    if (!commandOptions.skipInstall) {
      result.push({
        action: installDependencies,
        loadingText: 'Installing dependencies...',
        successText: 'Dependencies installed.',
      });
      result.push({
        action: () => generateAgentConfigs(packageFolder),
        loadingText: agentConfigsTaskText.loadingText,
        successText: agentConfigsTaskText.successText,
      });
    }

    if (!commandOptions.skipGit) {
      result.push({
        action: commitToGit,
        loadingText: 'Committing code...',
        successText: 'Code committed.',
      });
    }

    return result;
  }, [
    commandOptions.skipGit,
    commandOptions.skipInstall,
    commitToGit,
    generateProject,
    initializeGit,
    installDependencies,
    extendProject,
    packageFolder,
  ]);

  useAsync(async () => {
    await executeTaskRunner(tasks);
    onDone(true);
  }, []);

  return match(taskRunnerStatus, {
    Idle: () => null,
    Running: (status) => <GenerationProgress {...status} />,
    Done: (status) => (
      <Box flexDirection="column" marginBottom={1}>
        <GenerationProgress {...status} />
        <Box marginLeft={-2} marginTop={1}>
          <Alert type="success">
            <Badge skin="success">Success</Badge> <Text>Project set up.</Text>
          </Alert>
        </Box>
      </Box>
    ),
  });
};
